/**
 * RecurringOrderOptions Component
 *
 * Shows recurring order configuration options when "Make this recurring" is checked.
 * Part of US-RECURRING-ORDERS (Step 4).
 *
 * Features:
 * - Day-of-week multi-select (Mon-Sun using ISO weekdays 1-7)
 * - End type selection (Never, On date, After N occurrences)
 * - Date picker for end date
 * - Number input for occurrences count
 */

import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { DatePicker } from '@/components/ui/date-picker'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

// ISO weekdays: 1=Monday through 7=Sunday
const DAYS_OF_WEEK = [
  { value: 1, label: 'Mon', fullLabel: 'Monday' },
  { value: 2, label: 'Tue', fullLabel: 'Tuesday' },
  { value: 3, label: 'Wed', fullLabel: 'Wednesday' },
  { value: 4, label: 'Thu', fullLabel: 'Thursday' },
  { value: 5, label: 'Fri', fullLabel: 'Friday' },
  { value: 6, label: 'Sat', fullLabel: 'Saturday' },
  { value: 7, label: 'Sun', fullLabel: 'Sunday' }
]

/**
 * @param {Object} props
 * @param {number[]} props.selectedDays - ISO weekdays [1-7] that are selected
 * @param {function} props.onDaysChange - Callback when days selection changes
 * @param {string} props.endType - 'indefinite' | 'until_date' | 'after_count'
 * @param {function} props.onEndTypeChange - Callback when end type changes
 * @param {Date|null} props.endDate - End date for 'until_date' type
 * @param {function} props.onEndDateChange - Callback when end date changes
 * @param {number|null} props.occurrences - Count for 'after_count' type
 * @param {function} props.onOccurrencesChange - Callback when occurrences changes
 */
export default function RecurringOrderOptions({
  selectedDays,
  onDaysChange,
  endType,
  onEndTypeChange,
  endDate,
  onEndDateChange,
  occurrences,
  onOccurrencesChange
}) {
  /**
   * Toggle a day in the selection
   */
  const handleDayToggle = (dayValue) => {
    if (selectedDays.includes(dayValue)) {
      // Remove the day
      onDaysChange(selectedDays.filter(d => d !== dayValue))
    } else {
      // Add the day and sort
      onDaysChange([...selectedDays, dayValue].sort((a, b) => a - b))
    }
  }

  /**
   * Handle occurrences input change
   */
  const handleOccurrencesChange = (e) => {
    const value = e.target.value
    if (value === '') {
      onOccurrencesChange(null)
    } else {
      const num = parseInt(value, 10)
      if (!isNaN(num) && num >= 1) {
        onOccurrencesChange(num)
      }
    }
  }

  return (
    <div className="border rounded-md p-4 space-y-4 bg-muted/30">
      {/* Day-of-week selection */}
      <div className="space-y-1">
        <Label className="text-sm font-medium">Repeat every week on:</Label>
        <div className="flex flex-wrap gap-2">
          {DAYS_OF_WEEK.map((day) => {
            const isSelected = selectedDays.includes(day.value)
            return (
              <button
                key={day.value}
                type="button"
                onClick={() => handleDayToggle(day.value)}
                className={cn(
                  "inline-flex items-center justify-center h-8 px-3 text-sm font-medium rounded-md border transition-colors",
                  "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                  isSelected
                    ? "bg-black text-white border-black"
                    : "bg-white text-black border-input hover:bg-accent hover:text-accent-foreground"
                )}
                aria-pressed={isSelected}
                aria-label={`${isSelected ? 'Remove' : 'Add'} ${day.fullLabel}`}
              >
                {day.label}
              </button>
            )
          })}
        </div>
        {selectedDays.length === 0 && (
          <p className="text-sm text-muted-foreground">Select at least one day</p>
        )}
      </div>

      {/* End condition */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Ends:</Label>
        <RadioGroup value={endType} onValueChange={onEndTypeChange}>
          {/* Never option */}
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="indefinite" id="end-indefinite" />
            <Label htmlFor="end-indefinite" className="font-normal cursor-pointer">
              Never
            </Label>
          </div>

          {/* On date option */}
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="until_date" id="end-until-date" />
            <Label htmlFor="end-until-date" className="font-normal cursor-pointer flex items-center gap-2">
              On date:
              <DatePicker
                date={endDate}
                onDateChange={onEndDateChange}
                placeholder="Select end date"
                disabled={endType !== 'until_date'}
                className="w-[180px]"
                disabledBefore={new Date()}
              />
            </Label>
          </div>

          {/* After N occurrences option */}
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="after_count" id="end-after-count" />
            <Label htmlFor="end-after-count" className="font-normal cursor-pointer flex items-center gap-2">
              After
              <Input
                type="number"
                min="1"
                value={occurrences ?? ''}
                onChange={handleOccurrencesChange}
                disabled={endType !== 'after_count'}
                className="w-20"
                placeholder="1"
              />
              occurrences
            </Label>
          </div>
        </RadioGroup>
      </div>
    </div>
  )
}
